import { DateTime } from "luxon";
import { useState, useCallback } from "react";

import { Draggable, DraggableProps } from "@/common/components/Draggable/Draggable";


/**
 * @callback timeUpdate
 * @prop {DateTime} dt - The updated time caused by dragging the elment left or right
 * @returns {void}
 *
 * @typedef DraggableTimeProps
 * @extends DraggableProps
 * @prop {DateTime} time - Time
 * @prop {DateTime} maxTime - An updated time will have a max bound to the maxTime
 * @prop {DateTime} minTime - An updated time will have a min bound to the minTime
 * @prop {number} duration - Millisecond duration of the element
 * @prop {timeUpdate} timeUpdate - Callback called with updated time
 */
export interface DraggableTimeProps extends DraggableProps {
    time: DateTime,
    maxTime?: DateTime,
    minTime?: DateTime,
    duration: number,
    timeUpdate: (dt: DateTime) => void,
}

/**
 * A component that wraps the `Draggable` component in a time context.
 * Dragging the component to the left will increase the time.
 * Dragging the component to the right will increase the time.
 */
export const DraggableTime: React.FC<DraggableTimeProps> = ({
    children,
    minTime,
    maxTime,
    time,
    onDragMove,
    onDragEnd,
    onDragStart,
    timeUpdate,
    duration,
    ...rest
}) => {
    const [dragStartTime, setDragStartTime] = useState<null | DateTime>(null);

    const dragMove = useCallback((ev: any, diffTotal, diffLast) => {
        if (dragStartTime) {
            const changeTotal = -1 * duration * diffTotal.diffXRelative;
            let updatedTime = dragStartTime.plus({ milliseconds: changeTotal });

            if (maxTime) {
                updatedTime = DateTime.min(updatedTime, maxTime);
            }

            if (minTime) {
                updatedTime = DateTime.max(updatedTime, minTime);
            }

            timeUpdate(updatedTime);

            if (onDragMove) {
                onDragMove(ev, diffTotal, diffLast);
            }
        }
    }, [duration, dragStartTime, timeUpdate, maxTime, minTime]);

    const dragStart = useCallback((...args: Parameters<Required<DraggableProps>["onDragStart"]>) => {
        setDragStartTime(time);

        if (onDragStart) {
            onDragStart(...args);
        }
    }, [time, onDragStart]);

    const dragEnd = useCallback((...args: Parameters<Required<DraggableProps>["onDragEnd"]>) => {
        setDragStartTime(null);

        if (onDragEnd) {
            onDragEnd(...args);
        }
    }, [onDragEnd]);

    return (
        <Draggable
            {...rest}
            onDragStart={dragStart}
            onDragEnd={dragEnd}
            onDragMove={dragMove}
        >
            {children}
        </Draggable>
    );
};
